import _ from 'lodash'
import { createSelector } from 'reselect'
import { getLocale } from '../imports'

import { getActiveQueueName } from './queue/selectors'
import { getShowableNotifications } from './notifications/selectors'
import { getLocalizedMotds, getLatestMotd } from './motds/selectors'
import {
    getTimeoutBarVisibility,
    getRequestCountVisibility,
    getAscending,
    getMaxToShow,
} from './settings/selectors'

// Get all possibly showable notifications, and only return those that
// are either alwaysOnTop or are in the current queue
const getFilteredNotifications = createSelector(
    [getShowableNotifications, getActiveQueueName],
    (showableNotifications, queue) => {
        const result = _.pickBy(
            showableNotifications,
            notification => notification.alwaysOnTop || notification.queue === queue,
        )
        // console.log('getFilteredNotifications: ', showableNotifications, queue, result)
        return result
    },
)

const getFilteredMotds = createSelector(
    [getLocalizedMotds, getActiveQueueName],
    (showableMotds, queue) => {
        const result = _.pickBy(showableMotds, motd => motd.alwaysOnTop || motd.queue === queue)
        // console.log('getFilteredMotds: ', showableMotds, queue, result)
        return result
    },
)

// We only want the 2 latest motds
// except for the latest one, we might or might not want it...!
const getLatestMotds = createSelector([getFilteredMotds, getLatestMotd], (motds, latest) => {
    // console.log('getLatestMotds: ', motds, latest)
    if (_.size(motds) < 3) {
        return motds
    }

    let result = { ...motds }

    // If latest motd has already been seen
    // and we have at least 2 older unseen motds
    // then we want to show the 2 older ones
    if (latest !== undefined && latest.seen) {
        result = _.omit(result, latest.id)
    }

    // Sort
    const sorted = _.sortBy(result, toast => toast.updated)

    // Pick the top 2
    result = sorted.slice(0, 2)

    // Finally turn back into an object
    result = _.keyBy(result, toast => toast.id)

    // console.log('getLatestMotds: ', result)

    return result
})

const getLatestToasts = createSelector(
    [getFilteredNotifications, getLatestMotds],
    (notifications, motds) => {
        const result = {
            ...notifications,
            ...motds,
        }
        // console.log('getLatestToasts: ', notifications, motds, result)
        return result
    },
)

// Reduces the toasts from state into what I want to send to the component
const getFormattedToasts = createSelector(
    [getLatestToasts, getTimeoutBarVisibility, getRequestCountVisibility, getLocale],
    (toasts, timeoutBar, showCounter, locale) => {
        const res = _.reduce(
            toasts,
            (result, toast) => {
                result[toast.id] = {
                    id: toast.id,
                    title: toast.title,
                    content: toast.content,
                    isMessage: toast.isMessage,
                    timeout: toast.timeout,
                    timeoutBar,
                    type: toast.severity,
                    counts: {
                        total: toast.count,
                        sinceLast: toast.countSinceLastDismissal,
                    },
                    showCounter,
                    updated: toast.isMessage ? toast.motd.updated : '',
                    locale,
                }
                return result
            },
            {},
        )
        // console.log('getFormattedToasts: ', toasts, timeoutBar, showCounter, locale, res)
        return res
    },
)

const getSortedToasts = createSelector([getFormattedToasts, getAscending], (toasts, ascending) => {
    // console.log('getSortedToasts: ', toasts, ascending)
    const sorted = _.sortBy(toasts, toast => toast.updated)
    if (ascending) _.reverse(sorted)
    return sorted
})

// eslint-disable-next-line
export const getDisplayToasts = createSelector([getSortedToasts, getMaxToShow], (toasts, max) => {
    const result = toasts.slice(0, max)
    // console.log('getDisplayToasts: ', result, max)
    return result
})



// WEBPACK FOOTER //
// ./src/features/Toaster/data/selectors.js